import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useData } from "../data/DataContext"
import { ObjectiveModal } from "../components/ObjectiveModal"
import {
  AddObjectiveCard,
  ObjectiveCard,
  RagCountRing,
  buildRagIndex,
  countRags,
  getCurrentRag,
  getHistoryScores,
  reportingYearForDate,
  type RagIndex,
} from "../components/ObjectiveCard"
import type { Objective, ObjectiveStatus, Person } from "../data/types"

const STATUS_ORDER: Record<ObjectiveStatus, number> = { Active: 0, Draft: 1, Deleted: 2 }

type ModalState =
  | { kind: "none" }
  | { kind: "edit"; objective: Objective }
  | { kind: "create"; personName: string }

export function CoverPage() {
  const { objectives, ragScores, categories: taxonomyCategories, me, refresh } = useData()
  const [modal, setModal] = useState<ModalState>({ kind: "none" })

  const covers = useMemo(
    () => [...me.covers].sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [me.covers],
  )

  const reportingYear = reportingYearForDate()
  const ragIndex = useMemo(
    () => buildRagIndex(ragScores, reportingYear),
    [ragScores, reportingYear],
  )

  // Sort by taxonomy order — anything not in the taxonomy sinks to the bottom.
  const categoryOrder = useMemo(
    () => taxonomyCategories.map((c) => c.name),
    [taxonomyCategories],
  )
  const categoryIndex = useMemo(() => {
    const m = new Map<string, number>()
    categoryOrder.forEach((c, i) => m.set(c, i))
    return m
  }, [categoryOrder])

  const sortObjectives = (a: Objective, b: Objective): number => {
    const sa = a.status ? STATUS_ORDER[a.status] : 99
    const sb = b.status ? STATUS_ORDER[b.status] : 99
    if (sa !== sb) return sa - sb
    const ca = a.category ? categoryIndex.get(a.category) ?? 99 : 99
    const cb = b.category ? categoryIndex.get(b.category) ?? 99 : 99
    if (ca !== cb) return ca - cb
    return (a.year ?? 0) - (b.year ?? 0)
  }

  const categories = useMemo(() => {
    // Taxonomy order first, then in-use-but-not-in-taxonomy stragglers.
    const inUse = new Set<string>()
    for (const o of objectives) if (o.category) inUse.add(o.category)
    const taxonomy = new Set(categoryOrder)
    const extras = [...inUse].filter((c) => !taxonomy.has(c)).sort()
    return [...categoryOrder, ...extras]
  }, [objectives, categoryOrder])

  const yearLabel = `${reportingYear}/${(reportingYear + 1) % 100}`

  // Empty state — nobody to cover
  if (covers.length === 0) {
    return (
      <div className="rounded-lg border border-rule bg-card p-8 shadow-card">
        <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-brass">
          Nothing to cover
        </div>
        <h2 className="mb-3 font-display text-[22px] font-medium tracking-[-0.01em] text-ink">
          You're not covering anyone's objectives
        </h2>
        <p className="mb-2 max-w-xl text-[14px] leading-[1.6] text-ink-2">
          When a colleague doesn't log in themselves, an admin can assign you as their cover —
          you'll then maintain their objectives on their behalf and they'll appear here.
        </p>
        <p className="max-w-xl text-[14px] leading-[1.6] text-ink-2">
          To set this up, an admin opens the{" "}
          <Link to="/admin" className="text-brass underline-offset-2 hover:underline">
            Admin
          </Link>{" "}
          page, finds that colleague's row in the roster, and sets{" "}
          <span className="font-mono text-[12px] text-ink">Covered by</span> to your name.
        </p>
      </div>
    )
  }

  return (
    <div>
      {/* Page intro */}
      <section className="mb-[26px] rounded-lg border border-rule bg-card px-[26px] py-[22px] shadow-card">
        <h1 className="font-display text-[28px] font-medium leading-[1.15] tracking-[-0.015em] text-ink">
          People I <em className="font-normal italic text-brass">cover</em>
        </h1>
        <div className="mt-[6px] font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
          {covers.length} colleague{covers.length === 1 ? "" : "s"} &middot; {yearLabel} &middot;
          full edit rights
        </div>
        <p className="mt-[14px] max-w-2xl text-[13.5px] leading-[1.6] text-ink-2">
          You maintain these colleagues' objectives on their behalf. Changes you make save
          immediately against their record — they'll appear on their own page and in the team
          dashboard.
        </p>
      </section>

      {/* One block per covered person */}
      <div className="flex flex-col gap-[34px]">
        {covers.map((person) => (
          <CoveredPersonSection
            key={person.id}
            person={person}
            objectives={objectives
              .filter((o) => o.personName === person.fullName && o.status !== "Deleted")
              .sort(sortObjectives)}
            onEdit={(o) => setModal({ kind: "edit", objective: o })}
            onAdd={() => setModal({ kind: "create", personName: person.fullName })}
            yearLabel={yearLabel}
            reportingYear={reportingYear}
            ragIndex={ragIndex}
          />
        ))}
      </div>

      {/* Modals */}
      {modal.kind === "edit" ? (
        <ObjectiveModal
          mode="edit"
          objective={modal.objective}
          categories={categories}
          onClose={() => setModal({ kind: "none" })}
          onSaved={refresh}
        />
      ) : null}
      {modal.kind === "create" ? (
        <ObjectiveModal
          mode="create"
          personName={modal.personName}
          defaultYear={reportingYear}
          categories={categories}
          onClose={() => setModal({ kind: "none" })}
          onSaved={refresh}
        />
      ) : null}
    </div>
  )
}

/* ---------- Covered-person section ---------- */

function CoveredPersonSection({
  person,
  objectives,
  onEdit,
  onAdd,
  yearLabel,
  reportingYear,
  ragIndex,
}: {
  person: Person
  objectives: Objective[]
  onEdit: (o: Objective) => void
  onAdd: () => void
  yearLabel: string
  reportingYear: number
  ragIndex: RagIndex
}) {
  const counts = useMemo(() => countRags(objectives, ragIndex), [objectives, ragIndex])
  const teamPill =
    person.team === "Exec"
      ? "bg-[#E8E1FC] text-[#4B3F90]"
      : person.team === "CLT"
        ? "bg-[rgb(185_138_58/0.12)] text-brass"
        : "bg-rag-unset-bg text-ink-3"

  return (
    <section>
      {/* Sub-hero */}
      <div className="mb-[18px] flex items-start justify-between gap-[30px] rounded-lg border border-rule bg-card px-[22px] py-[18px] shadow-card">
        <div className="min-w-0">
          <div className="mb-[6px] flex items-center gap-[10px]">
            <h2 className="font-display text-[22px] font-medium leading-[1.15] tracking-[-0.015em] text-ink">
              {person.fullName}
            </h2>
            {person.team ? (
              <span
                className={`rounded-full px-[9px] py-[2px] text-[11px] font-medium ${teamPill}`}
              >
                {person.team}
              </span>
            ) : null}
            <span className="rounded-full bg-[rgb(185_138_58/0.12)] px-[9px] py-[2px] font-mono text-[10px] uppercase tracking-[0.12em] text-brass">
              Covered by you
            </span>
          </div>
          {person.jobTitle ? (
            <div className="text-[13.5px] text-ink-2">{person.jobTitle}</div>
          ) : null}
          <div className="mt-[6px] font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
            {person.department ?? "No department"} &middot; {yearLabel} &middot;{" "}
            {objectives.length} objective{objectives.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="self-stretch w-px bg-rule" />

        <div className="flex flex-shrink-0 flex-col items-end gap-[10px]">
          <RagCountRing counts={counts} />
          <Link
            to={`/person/${person.id}`}
            className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-3 transition hover:text-brass"
          >
            View full page &rarr;
          </Link>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 gap-[18px]">
        {objectives.map((o) => (
          <ObjectiveCard
            key={o.id}
            objective={o}
            rag={getCurrentRag(o, ragIndex)}
            scores={getHistoryScores(o, ragIndex)}
            reportingYear={reportingYear}
            editable
            onClick={() => onEdit(o)}
          />
        ))}
        <AddObjectiveCard onClick={onAdd} />
      </div>
    </section>
  )
}
